"use client";

import { motion, useSpring } from "motion/react";
import { useEffect, useMemo, useRef } from "react";
const TICKER_ITEM_HEIGHT = 24;
export interface DateTickerProps {
  currentIndex: number;
  labels: string[];
  visible: boolean;
}
export function DateTicker({
  currentIndex,
  labels,
  visible
}: DateTickerProps) {
  // Parse labels into month and day parts
  const parsedLabels = useMemo(() => {
    return labels.map(label => {
      const parts = label.split(" ");
      const month = parts[0] || "";
      const day = parts[1] || "";
      return {
        month,
        day,
        full: label
      };
    });
  }, [labels]);

  // Get unique months and their indices
  const monthIndices = useMemo(() => {
    const uniqueMonths: string[] = [];
    const indices: number[] = [];
    parsedLabels.forEach((label_0, index) => {
      if (uniqueMonths.length === 0 || uniqueMonths.at(-1) !== label_0.month) {
        uniqueMonths.push(label_0.month);
        indices.push(index);
      }
    });
    return {
      uniqueMonths,
      indices
    };
  }, [parsedLabels]);

  // Find current month index
  const currentMonthIndex = useMemo(() => {
    if (currentIndex < 0 || currentIndex >= parsedLabels.length) {
      return 0;
    }
    const currentMonth = parsedLabels[currentIndex]?.month;
    return monthIndices.uniqueMonths.indexOf(currentMonth || "");
  }, [currentIndex, parsedLabels, monthIndices]);

  // Track previous month index
  const prevMonthIndexRef = useRef(-1);

  // Animated Y offsets
  const dayY = useSpring(0, {
    stiffness: 400,
    damping: 35
  });
  const monthY = useSpring(0, {
    stiffness: 400,
    damping: 35
  });

  // Update day scroll position
  useEffect(() => {
    dayY.set(-currentIndex * TICKER_ITEM_HEIGHT);
  }, [currentIndex, dayY]);

  // Update month scroll position only when month changes
  useEffect(() => {
    if (currentMonthIndex >= 0) {
      const isFirstRender = prevMonthIndexRef.current === -1;
      const monthChanged = prevMonthIndexRef.current !== currentMonthIndex;
      if (isFirstRender || monthChanged) {
        monthY.set(-currentMonthIndex * TICKER_ITEM_HEIGHT);
        prevMonthIndexRef.current = currentMonthIndex;
      }
    }
  }, [currentMonthIndex, monthY]);
  if (!visible || labels.length === 0) {
    return null;
  }
  return <motion.div className="bg-ds-neutral-900 dark:bg-ds-neutral-100 dark:text-ds-text-primary shadow-ds-elev-2 overflow-hidden rounded-full px-4 py-1 text-white" layout transition={{
    layout: {
      type: "spring",
      stiffness: 400,
      damping: 35
    }
  }}>
      <div className="relative h-6 overflow-hidden">
        <div className="flex items-center justify-center gap-1">
          {/* Month stack */}
          <div className="relative h-6 overflow-hidden">
            <motion.div className="flex flex-col" style={{
            y: monthY
          }}>
              {monthIndices.uniqueMonths.map(month_0 => <div className="flex h-6 shrink-0 items-center justify-center" key={month_0}>
                  <span className="whitespace-nowrap text-sm font-medium">{month_0}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Day stack */}
          <div className="relative h-6 overflow-hidden">
            <motion.div className="flex flex-col" style={{
            y: dayY
          }}>
              {parsedLabels.map(label_1 => <div className="flex h-6 shrink-0 items-center justify-center" key={label_1.full}>
                  <span className="whitespace-nowrap text-sm font-medium">{label_1.day}</span>
                </div>)}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>;
}
DateTicker.displayName = "DateTicker";
export default DateTicker;